<script setup lang="ts">
import { computed } from 'vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { NButton, NIcon } from 'naive-ui';
import { IconAdd } from '@/components/design/icons';
import type { TemplateField } from '@/api';
import { buildDefaultItem } from '@/composables/contractGeneration/useExtractedSchema';
import { useTemplateFieldMetadataMap } from '@/composables/contractGeneration/useTemplateFieldMetadata';
import { orderSiblings } from '@/utils/contractGeneration/templateFieldLabels';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import SchemaField from './SchemaField.vue';

interface Props {
  field: TemplateField;
  modelValue: unknown;
  rootFormData: Record<string, unknown>;
  /**
   * Scope chain of this object's own enclosing scopes (from innermost to
   * outermost). We prepend this object's current record when we pass
   * `localScopes` down to each child `SchemaField`, so a child condition
   * can reach siblings (inner), the object itself (via fieldKey paths),
   * or an outer loop / object / root (walked in order).
   */
  parentScopes?: unknown[];
  path: string;
  invalidPaths: Set<string>;
  disabled?: boolean;
  /** Root-level object fields get no card styling — they're flat on the page. */
  flat?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown> | null];
}>();

const childFields = computed(() => props.field.fields ?? {});

// Children in the author's order; the group's parent is this object's path.
const metadataMap = useTemplateFieldMetadataMap();
const orderedChildren = computed(() =>
  orderSiblings(Object.entries(childFields.value), props.path, metadataMap.value)
);

const isOptionalToggleable = computed<boolean>(() => {
  return !props.field.required && Boolean(props.field.condition_ast);
});

const isEnabled = computed<boolean>(() => {
  if (!isOptionalToggleable.value) return true;
  return props.modelValue !== null && props.modelValue !== undefined;
});

const currentRecord = computed<Record<string, unknown>>(() => {
  const value = props.modelValue;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
});

function handleChildUpdate(childKey: string, childValue: unknown): void {
  const next = { ...currentRecord.value, [childKey]: childValue };
  emit('update:modelValue', next);
}

function enableOptional(): void {
  emit('update:modelValue', buildDefaultItem(childFields.value));
}

function disableOptional(): void {
  emit('update:modelValue', null);
}

// --- chrome (tokens for the scoped styles) ---
const gBorder = gaiaNeutral.borderCard;
const gSoft = gaiaNeutral.surfaceSoft;
</script>

<template>
  <div class="object-field" :class="{ 'object-field--card': !flat }">
    <div v-if="isOptionalToggleable && !isEnabled" class="toggle-empty">
      <NButton size="small" dashed :disabled="disabled" @click="enableOptional">
        <template #icon>
          <NIcon><IconAdd /></NIcon>
        </template>
        Add details
      </NButton>
    </div>

    <template v-else>
      <div v-if="isOptionalToggleable" class="toggle-remove">
        <gaia-delete-button
          intent="remove"
          variant="label"
          size="xs"
          :disabled="disabled"
          @click="disableOptional"
        />
      </div>

      <div class="object-field__children">
        <SchemaField
          v-for="([childKey, childField], childIndex) in orderedChildren"
          :key="childKey"
          :field-key="childKey"
          :field="childField"
          :model-value="currentRecord[childKey]"
          :root-form-data="rootFormData"
          :local-scopes="[currentRecord, ...(parentScopes ?? [])]"
          :path="`${path}.${childKey}`"
          :invalid-paths="invalidPaths"
          :disabled="disabled"
          :style="{ '--schema-field-delay': `${childIndex * 120}ms` }"
          @update:model-value="value => handleChildUpdate(childKey, value)"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.object-field--card {
  border: 1px solid v-bind(gBorder);
  border-radius: 8px;
  padding: 16px;
  background: v-bind(gSoft);
}

.object-field__children {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toggle-empty {
  padding: 4px 0;
}

.toggle-remove {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
</style>
